import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'
import { authEventsApi } from '@/api/authEvents'
import { useEnumsStore } from '@/stores/enumsStore'
import { AUTH_EVENT_TYPES, type AuthEvent, type AuthEventType, type AuthEventsMeta } from '@/types/authEvents'
import { formatDateTimeSecondsDMY } from '@/utils/date'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { AuthEventDetailsDialog } from '@/components/admin/users/AuthEventDetailsDialog'
import {
  AUTH_EVENT_COLOR,
  authEventChipVariant,
  describePriorState,
  describeReason,
  eventTypeLabel,
  shortTabId,
  shortUserAgent,
} from '@/components/admin/users/authEventsUi'

type EventFilter = AuthEventType | 'all'

const DEFAULT_PER_PAGE = 25
const EMPTY_META: AuthEventsMeta = { current_page: 1, last_page: 1, per_page: DEFAULT_PER_PAGE, total: 0 }

interface AdminUserAuthEventsTabProps {
  userId: number
}

/**
 * Вкладка «Входы и выходы» на странице пользователя: кто, когда, с какого
 * устройства и вкладки вошёл, продлил сессию или потерял её — и почему.
 */
export function AdminUserAuthEventsTab({ userId }: AdminUserAuthEventsTabProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const enums = useEnumsStore((state) => state.enums)
  const loadEnums = useEnumsStore((state) => state.loadEnums)

  const [rows, setRows] = useState<AuthEvent[]>([])
  const [meta, setMeta] = useState<AuthEventsMeta>(EMPTY_META)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)
  const [eventFilter, setEventFilter] = useState<EventFilter>('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [menuEvent, setMenuEvent] = useState<AuthEvent | null>(null)
  const [detailsEvent, setDetailsEvent] = useState<AuthEvent | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)

  useEffect(() => {
    if (!enums) {
      void loadEnums()
    }
  }, [enums, loadEnums])

  useEffect(() => {
    // Ответ, пришедший после смены фильтра или страницы, не перетирает таблицу
    let active = true
    setLoading(true)
    setError(null)
    authEventsApi
      .listForUser(userId, {
        event: eventFilter === 'all' ? undefined : eventFilter,
        page,
        per_page: perPage,
      })
      .then((result) => {
        if (!active) return
        setRows(result.data)
        setMeta(result.meta)
      })
      .catch((err: unknown) => {
        if (!active) return
        setError(displayErrorMessage(getErrorMessage(err), t('admin.users.authEvents.loadFailed')))
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [eventFilter, page, perPage, t, userId])

  const openMenu = (anchor: HTMLElement, event: AuthEvent) => {
    setMenuAnchor(anchor)
    setMenuEvent(event)
  }

  const eventChip = (event: AuthEvent) => (
    <Chip
      size="small"
      color={AUTH_EVENT_COLOR[event.event]}
      variant={authEventChipVariant(event.event)}
      label={eventTypeLabel(enums, event.event)}
    />
  )

  const reasonCell = (event: AuthEvent) => {
    const reason = describeReason(event, enums, t)

    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
          {reason ?? '—'}
        </Typography>
        {event.blacklisted_ago_s !== null && (
          <Typography variant="caption" component="div" color="text.secondary">
            {t('admin.users.authEvents.blacklistedAgo', {
              time: t('admin.users.authEvents.seconds', { count: event.blacklisted_ago_s }),
            })}
          </Typography>
        )}
        {event.prior_state && (
          <Typography variant="caption" component="div" color="text.secondary">
            {t('admin.users.authEvents.priorState', { state: describePriorState(event.prior_state, t) })}
          </Typography>
        )}
      </Box>
    )
  }

  const clientCell = (event: AuthEvent) => {
    const tab = shortTabId(event.tab_id)
    const client = [event.client, event.client_version].filter(Boolean).join(' · ')

    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
          {client || '—'}
        </Typography>
        {tab && (
          <Typography variant="caption" component="div" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {t('admin.users.authEvents.tab', { id: tab })}
          </Typography>
        )}
      </Box>
    )
  }

  const deviceCell = (event: AuthEvent) => {
    const device = shortUserAgent(event.user_agent) ?? event.user_agent

    return (
      <Box sx={{ minWidth: 0, maxWidth: 260 }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
          {event.ip ?? '—'}
        </Typography>
        {device && (
          <Typography
            variant="caption"
            component="div"
            color="text.secondary"
            noWrap
            title={event.user_agent ?? undefined}
          >
            {device}
          </Typography>
        )}
      </Box>
    )
  }

  const actionsButton = (event: AuthEvent) => (
    <IconButton
      size="small"
      aria-label={t('admin.users.actions')}
      onClick={(clickEvent) => openMenu(clickEvent.currentTarget, event)}
    >
      <MoreVertIcon fontSize="small" />
    </IconButton>
  )

  const tableHead = (
    <TableHead>
      <TableRow>
        <TableCell>{t('admin.users.authEvents.columnTime')}</TableCell>
        <TableCell>{t('admin.users.authEvents.columnEvent')}</TableCell>
        <TableCell>{t('admin.users.authEvents.columnReason')}</TableCell>
        <TableCell>{t('admin.users.authEvents.columnClient')}</TableCell>
        <TableCell>{t('admin.users.authEvents.columnDevice')}</TableCell>
        <TableCell align="right">{t('admin.users.actions')}</TableCell>
      </TableRow>
    </TableHead>
  )

  const renderSkeleton = () =>
    isMobile ? (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[1, 2, 3].map((i) => (
          <Paper key={i} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Skeleton variant="rounded" width={120} height={24} />
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
            <Skeleton variant="text" width="50%" height={20} />
            <Skeleton variant="text" width="80%" height={20} />
            <Skeleton variant="text" width="60%" height={20} />
          </Paper>
        ))}
      </Box>
    ) : (
      <TableContainer component={Paper}>
        <Table>
          {tableHead}
          <TableBody>
            {[1, 2, 3, 4, 5].map((i) => (
              <TableRow key={i}>
                <TableCell><Skeleton variant="text" width={140} /></TableCell>
                <TableCell><Skeleton variant="rounded" width={110} height={24} /></TableCell>
                <TableCell><Skeleton variant="text" width={200} /></TableCell>
                <TableCell><Skeleton variant="text" width={90} /></TableCell>
                <TableCell><Skeleton variant="text" width={160} /></TableCell>
                <TableCell align="right"><Skeleton variant="circular" width={32} height={32} sx={{ ml: 'auto' }} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )

  const renderRows = () =>
    isMobile ? (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {rows.map((event) => (
          <Paper key={event.id} sx={{ p: 2, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                {eventChip(event)}
                <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
                  {formatDateTimeSecondsDMY(event.created_at)}
                </Typography>
              </Box>
              {actionsButton(event)}
            </Box>
            {reasonCell(event)}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
              {clientCell(event)}
              {deviceCell(event)}
            </Box>
          </Paper>
        ))}
      </Box>
    ) : (
      <TableContainer component={Paper}>
        <Table>
          {tableHead}
          <TableBody>
            {rows.map((event) => (
              <TableRow key={event.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTimeSecondsDMY(event.created_at)}</TableCell>
                <TableCell>{eventChip(event)}</TableCell>
                <TableCell>{reasonCell(event)}</TableCell>
                <TableCell>{clientCell(event)}</TableCell>
                <TableCell>{deviceCell(event)}</TableCell>
                <TableCell align="right">{actionsButton(event)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )

  return (
    <Box sx={{ minWidth: 0 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 240 } }}>
          <InputLabel id="auth-events-filter">{t('admin.users.authEvents.filterEvent')}</InputLabel>
          <Select
            labelId="auth-events-filter"
            label={t('admin.users.authEvents.filterEvent')}
            value={eventFilter}
            onChange={(event) => {
              setEventFilter(event.target.value as EventFilter)
              setPage(1)
            }}
          >
            <MenuItem value="all">{t('admin.users.authEvents.allEvents')}</MenuItem>
            {AUTH_EVENT_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {eventTypeLabel(enums, type)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {t('admin.users.authEvents.retention')}
        </Typography>
      </Box>

      {loading ? (
        renderSkeleton()
      ) : meta.total === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">{t('admin.users.authEvents.empty')}</Typography>
        </Paper>
      ) : (
        renderRows()
      )}

      {meta.total > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', mt: 2, minWidth: 0, overflow: 'auto' }}>
          <TablePagination
            component="div"
            count={meta.total}
            page={Math.max(0, page - 1)}
            onPageChange={(_, newPage) => setPage(newPage + 1)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(event) => {
              setPerPage(parseInt(event.target.value, 10))
              setPage(1)
            }}
            rowsPerPageOptions={[10, 15, 25, 50]}
            labelRowsPerPage={isMobile ? '' : t('cards.rows_per_page')}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} ${t('cards.of')} ${count !== -1 ? count : `${t('cards.more_than')} ${to}`}`
            }
            sx={{ '& .MuiTablePagination-toolbar': { flexWrap: 'wrap' } }}
          />
        </Box>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        TransitionProps={{ onExited: () => setMenuEvent(null) }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            if (menuEvent) {
              setDetailsEvent(menuEvent)
              setDetailsOpen(true)
            }
            setMenuAnchor(null)
          }}
        >
          {t('common.details')}
        </MenuItem>
      </Menu>

      <AuthEventDetailsDialog
        event={detailsEvent}
        open={detailsOpen}
        enums={enums}
        onClose={() => setDetailsOpen(false)}
      />
    </Box>
  )
}
